import { cartLineId } from './household';
import { ingredientConflictsWithMember, memberConflictLabels } from './memberRestrictions';
import { computeAutoSubstitution } from './autoSubstitution';

/** Builds the cart-line record for a dish — one independently-customizable
 *  serving. Any ingredient that conflicts with the member's allergies/diet is
 *  pre-deselected and a non-conflicting replacement from the same category is
 *  filled in, so the dish never arrives incomplete. Guests have no
 *  premium-protein tier, so the replacement pool excludes premium (upcharge)
 *  proteins.
 *
 *  Lives here rather than in FlowContext because MenuPage builds the same record
 *  three times over: the meal card's preview, the uncommitted *draft* the
 *  customer configures, and the line that lands in the cart. The lineId doubles
 *  as the substitution seed so all three pick the same replacements. */
/** GET /api/menu sends a dish's `originalIngredients` as bare `{ id, name }` —
 *  the Airtable "Allergen" links only ride along on the option records
 *  (proteinOptions, veggieOptions, …), and every original ingredient appears in
 *  one of those pools. Without this the allergy check sees no allergens on the
 *  dish as designed and nothing is ever detected. */
function withAllergenLinks(ingredients, dish) {
  const pool = [
    ...(dish.proteinOptions || []),
    ...(dish.veggieOptions || []),
    ...(dish.starchOptions || []),
    ...(dish.sauceOptions || []),
    ...(dish.garnishOptions || []),
  ];
  return (ingredients || []).map((ingredient) => {
    if (ingredient.allergen) return ingredient;
    const option = pool.find((opt) => opt.id != null && opt.id === ingredient.id);
    return option ? { ...ingredient, allergen: option.allergen || [] } : ingredient;
  });
}

/** What the customer has currently selected on a line, with allergen links
 *  attached. A line stores the same option pools a dish does, so this also covers
 *  lines saved before those links were carried through. */
export function lineIngredients(line) {
  return withAllergenLinks(line?.finalIngredients, line || {});
}

export function buildCartLine(dish, memberId, member) {
  const originalIngredients = withAllergenLinks(dish.originalIngredients, dish);
  const lineId = cartLineId(memberId, dish.dishId, dish.mealType);
  const result = computeAutoSubstitution({
    originalIngredients,
    proteinOptions: dish.proteinOptions,
    veggieOptions: dish.veggieOptions,
    starchOptions: dish.starchOptions,
    sauceOptions: dish.sauceOptions,
    garnishOptions: dish.garnishOptions,
    conflictsFn: (ingredient) => ingredientConflictsWithMember(ingredient, member),
    conflictLabelsFn: (ingredient) => memberConflictLabels(ingredient, member),
    isPremiumCustomer: false,
    seed: lineId,
  });
  return {
    lineId,
    dishId: dish.dishId,
    memberId,
    name: dish.name,
    mealType: dish.mealType,
    price: dish.price,
    imageUrl: dish.imageUrl,
    quantity: 1,
    originalIngredients,
    finalIngredients: result.finalIngredients,
    proteinOptions: result.proteinOptions,
    veggieOptions: result.veggieOptions,
    starchOptions: result.starchOptions,
    sauceOptions: result.sauceOptions,
    garnishOptions: result.garnishOptions,
    // What the auto-substitution did, which is history and so belongs on the
    // line. Whether the line *currently* conflicts is not stored: it depends on
    // what's selected now and who it's for, both of which keep changing, so it's
    // derived where it's shown — see memberIngredientConflicts.
    autoSubstitutions: result.substitutions,
    allergyAdjusted: result.wasAdjusted,
    selectionResolved: true,
    totalOriginalVeggie: dish.totalOriginalVeggie || 0,
  };
}

/** A second serving of a line the customer already has: same configuration, its
 *  own id so it can be customized independently. Duplicating an already-added
 *  serving isn't a fresh allergy substitution event, so the original's record is
 *  dropped and the "swapped in" badges don't reappear on the copy. */
export function duplicateCartLine(line) {
  const suffix = `dup-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    ...line,
    lineId: `${line.lineId}::${suffix}`,
    quantity: 1,
    autoSubstitutions: [],
  };
}

/** Re-keys a line for a different household member. The member id is part of the
 *  lineId, so reassigning a meal means re-keying the line rather than just
 *  setting a field — otherwise the line's id would disagree with its memberId
 *  and the modal would lose track of which line it is editing. Any "::dup-…"
 *  suffix is preserved so duplicated servings stay distinct. */
export function relineForMember(line, memberId) {
  const suffix = line.lineId.split('::').slice(3).join('::');
  const base = cartLineId(memberId, line.dishId, line.mealType);
  return { ...line, memberId, lineId: suffix ? `${base}::${suffix}` : base };
}

/** Forgets what the auto-substitution did for the member a line used to belong
 *  to: "swapped in X for your allergy" describes their allergies, not the new
 *  recipient's. The ingredients are untouched — reassigning a meal never
 *  re-adjusts it — and whether it conflicts for whoever holds it now is derived
 *  at display time, so there's no flag here to keep in sync. */
export function clearAdjustmentHistory(line) {
  return { ...line, autoSubstitutions: [], allergyAdjusted: false };
}
